import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { CareShell, Panel } from "../../components/care/CareLayout";
import ResidentProfilePhoto from "../../components/care/ResidentProfilePhoto";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import Guard from "../../components/access-control/rbac/Guard";
import { careService, MAX_WORKSPACE_RESIDENTS } from "../../services/careService";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { useFileUpload } from "../../hooks/useFileUpload";

const RESIDENT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const EXPRESS_ONBOARDING_STEPS = ["Resident", "Contact", "Emergency Contact", "Doctor / Physician", "Social Worker / Case Manager", "Residency", "Confirm"];
const EXPRESS_ONBOARDING_STEP_SLUGS = ["resident", "contact", "emergency-contact", "doctor-physician", "social-worker-case-manager", "residency", "confirm"];
const PRIMARY_LANGUAGE_OPTIONS = [
  "English", "Spanish", "Chinese", "Tagalog", "Vietnamese", "Arabic", "French",
  "Korean", "Russian", "German", "Hindi", "Portuguese", "Japanese", "Farsi",
  "Amharic", "Somali", "Swahili", "American Sign Language", "Other",
];
const RACE_OPTIONS = ["American Indian or Alaska Native", "Asian", "Black or African American", "Hispanic or Latino", "Middle Eastern or North African", "Native Hawaiian or Other Pacific Islander", "White", "Multiracial", "Other", "Prefer not to say", "Unknown"];
const MARITAL_STATUS_OPTIONS = ["Single", "Married", "Domestic partnership", "Separated", "Divorced", "Widowed", "Other", "Prefer not to say", "Unknown"];
const CONTACT_RELATIONSHIP_OPTIONS = [
  "Spouse", "Daughter", "Son", "Parent", "Sibling", "Grandchild",
  "Emergency Contact", "Power of Attorney", "Legal Guardian", "Friend",
  "Case Manager", "Social Worker / Case Manager", "Financial Worker",
  "Nurse Delegator", "Pharmacy", "Primary Care Doctor", "Specialist", "Other",
];
const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const fullNameFor = (resident = {}) =>
  resident.name ||
  resident.fullName ||
  resident.residentName ||
  [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" ") ||
  "Unnamed resident";
const locationFor = (resident = {}) => resident.locationInformation || resident.address || resident.location || {};
const normalizeResident = (resident = {}) => {
  const name = fullNameFor(resident);
  const location = locationFor(resident);
  return {
    id: resident.id || resident.residentId,
    initials: resident.initials || initialsFor(name),
    name,
    preferredName: resident.preferredName || resident.nickname || "",
    gender: resident.gender || resident.sex || "",
    city: location.city || resident.city || "",
    state: location.state || resident.state || "",
    room: resident.room || resident.roomNumber || "",
    ncpStatus: resident.ncpStatusLabel || resident.ncpStatus || resident.carePlanStatus || "Current",
    alerts: Number(resident.alerts || resident.openAlerts || resident.openAlertCount || 0),
    status: String(resident.status || "").toUpperCase(),
    onboardingStatus: String(resident.onboardingStatus || resident.onboardingStepStatus?.status || "").toUpperCase(),
  };
};

const residentHref = (resident) => `/residents/${resident.id || resident.name}`;
const expressResidentForm = () => ({
  residentType: "EXISTING",
  firstName: "",
  middleName: "",
  lastName: "",
  preferredName: "",
  dateOfBirth: "",
  gender: "",
  primaryLanguage: "English",
  heightFeet: "",
  heightInches: "",
  weightLbs: "",
  race: "",
  raceOther: "",
  maritalStatus: "",
  placeOfBirth: "",
  religion: "",
  phone: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  county: "",
  state: "WA",
  country: "US",
  postalCode: "",
  latitude: null,
  longitude: null,
  placeId: "",
  emergencyContactName: "",
  emergencyContactRelationship: "",
  emergencyContactRelationshipOther: "",
  emergencyContactPhone: "",
  emergencyContactFax: "",
  emergencyContactEmail: "",
  physicianFirstName: "",
  physicianLastName: "",
  physicianType: "Primary Care Doctor",
  physicianPractice: "",
  physicianPhone: "",
  physicianFax: "",
  physicianEmail: "",
  physicianAddressLine1: "",
  physicianAddressLine2: "",
  physicianCity: "",
  physicianCounty: "",
  physicianState: "WA",
  physicianCountry: "US",
  physicianPostalCode: "",
  physicianLatitude: null,
  physicianLongitude: null,
  physicianPlaceId: "",
  socialWorkerFirstName: "",
  socialWorkerLastName: "",
  socialWorkerAgency: "",
  socialWorkerRelationship: "Social Worker / Case Manager",
  socialWorkerRelationshipOther: "",
  socialWorkerMobilePhone: "",
  socialWorkerOfficePhone: "",
  socialWorkerHomePhone: "",
  socialWorkerFax: "",
  socialWorkerPreferredPhone: "mobilePhone",
  socialWorkerEmail: "",
  socialWorkerNotes: "",
  socialWorkerAddressLine1: "",
  socialWorkerAddressLine2: "",
  socialWorkerCity: "",
  socialWorkerCounty: "",
  socialWorkerState: "WA",
  socialWorkerCountry: "US",
  socialWorkerPostalCode: "",
  socialWorkerLatitude: null,
  socialWorkerLongitude: null,
  socialWorkerPlaceId: "",
  room: "",
  moveInDate: new Date().toISOString().slice(0, 10),
  residencyNotes: "",
  informationOnFileConfirmed: false,
});
const createdResidentId = (resident = {}) => resident.id || resident.residentId || resident.clientId || "";
const extractionFieldsFrom = (document = {}) => (Array.isArray(document?.sections) ? document.sections : []).flatMap((section) =>
  (section?.subsections || []).flatMap((subsection) => (subsection?.fields || [])));
const extractedValue = (fields, patterns) => String(fields.find((field) => patterns.some((pattern) => pattern.test(String(field?.label || ""))))?.value || "").trim();
const normalizedExtractedDate = (value) => {
  const text = String(value || "").trim();
  const iso = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;
  const us = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (!us) return "";
  return `${us[3].length === 2 ? `19${us[3]}` : us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
};
const expressFormFromExtraction = (document = {}) => {
  const fields = extractionFieldsFrom(document);
  const fullName = extractedValue(fields, [/^(client|resident)\s+(full\s+)?name$/i, /^full\s+name$/i]);
  const names = fullName.split(/\s+/).filter(Boolean);
  const gender = extractedValue(fields, [/^(client|resident)\s+(sex|gender)$/i, /^(sex|gender)$/i]).toLowerCase();
  return {
    firstName: extractedValue(fields, [/first\s+name/i]) || names[0] || "",
    middleName: extractedValue(fields, [/middle\s+name/i]) || (names.length > 2 ? names.slice(1, -1).join(" ") : ""),
    lastName: extractedValue(fields, [/last\s+name/i]) || (names.length > 1 ? names[names.length - 1] : ""),
    preferredName: extractedValue(fields, [/preferred\s+name/i, /nickname/i]),
    dateOfBirth: normalizedExtractedDate(extractedValue(fields, [/date\s+of\s+birth/i, /^dob$/i, /birth\s+date/i])),
    gender: /female/.test(gender) ? "FEMALE" : /male/.test(gender) ? "MALE" : /non.?binary/.test(gender) ? "NON_BINARY" : "",
    primaryLanguage: extractedValue(fields, [/preferred\s+language/i, /primary\s+language/i, /^language$/i]),
    phone: extractedValue(fields, [/resident.*phone/i, /client.*phone/i, /^phone$/i]),
    email: extractedValue(fields, [/resident.*email/i, /client.*email/i, /^email$/i]),
    addressLine1: extractedValue(fields, [/resident.*address/i, /client.*address/i, /^address$/i]),
    city: extractedValue(fields, [/^city$/i]),
    state: extractedValue(fields, [/^state$/i]),
    postalCode: extractedValue(fields, [/zip/i, /postal/i]),
    emergencyContactName: extractedValue(fields, [/emergency.*contact.*name/i]),
    emergencyContactRelationship: extractedValue(fields, [/emergency.*contact.*relationship/i]),
    emergencyContactPhone: extractedValue(fields, [/emergency.*contact.*phone/i]),
    emergencyContactEmail: extractedValue(fields, [/emergency.*contact.*email/i]),
    socialWorkerFirstName: extractedValue(fields, [/social.*worker.*first.*name/i, /case.*manager.*first.*name/i]),
    socialWorkerLastName: extractedValue(fields, [/social.*worker.*last.*name/i, /case.*manager.*last.*name/i]),
    socialWorkerAgency: extractedValue(fields, [/social.*worker.*agency/i, /case.*manager.*agency/i, /social.*worker.*organization/i]),
    socialWorkerMobilePhone: extractedValue(fields, [/social.*worker.*mobile/i, /case.*manager.*mobile/i, /social.*worker.*phone/i]),
    socialWorkerOfficePhone: extractedValue(fields, [/social.*worker.*office.*phone/i, /case.*manager.*office.*phone/i]),
    socialWorkerEmail: extractedValue(fields, [/social.*worker.*email/i, /case.*manager.*email/i]),
    room: extractedValue(fields, [/room.*number/i, /^room$/i]),
    moveInDate: normalizedExtractedDate(extractedValue(fields, [/move.?in.*date/i, /admission.*date/i])),
  };
};
const addressFromGooglePlace = (details = {}) => {
  const components = Array.isArray(details.address_components) ? details.address_components : [];
  const valueFor = (...types) => components.find((component) => types.some((type) => component.types?.includes(type)))?.long_name || "";
  const shortFor = (...types) => components.find((component) => types.some((type) => component.types?.includes(type)))?.short_name || "";
  const streetNumber = valueFor("street_number");
  const route = valueFor("route");
  const latitudeValue = details.geometry?.location?.lat;
  const longitudeValue = details.geometry?.location?.lng;
  return {
    addressLine1: [streetNumber, route].filter(Boolean).join(" ") || details.formatted_address || "",
    city: valueFor("locality", "postal_town", "sublocality") || valueFor("administrative_area_level_2"),
    county: valueFor("administrative_area_level_2"),
    state: shortFor("administrative_area_level_1"),
    country: shortFor("country"),
    postalCode: valueFor("postal_code"),
    latitude: typeof latitudeValue === "function" ? latitudeValue() : latitudeValue ?? null,
    longitude: typeof longitudeValue === "function" ? longitudeValue() : longitudeValue ?? null,
    placeId: details.place_id || "",
  };
};

export default function ResidentsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const { uploadFile } = useFileUpload();
  const googleMapsApiKey = appConfig?.googleMapsApiKey || appConfig?.googleMapsApiMatrixKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [residents, setResidents] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expressOpen, setExpressOpen] = useState(false);
  const [expressForm, setExpressForm] = useState(expressResidentForm);
  const [expressStep, setExpressStep] = useState(0);
  const [expressSaving, setExpressSaving] = useState(false);
  const [expressError, setExpressError] = useState("");
  const [assessmentFile, setAssessmentFile] = useState(null);
  const [assessmentExtraction, setAssessmentExtraction] = useState(null);
  const [assessmentImporting, setAssessmentImporting] = useState(false);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const onboardingParam = searchParams.get("onboarding");
  const onboardingStepParam = searchParams.get("step");

  useEffect(() => {
    if (onboardingParam !== "express") {
      setExpressOpen(false);
      return;
    }
    const requestedStep = EXPRESS_ONBOARDING_STEP_SLUGS.indexOf(onboardingStepParam || "resident");
    setExpressOpen(true);
    setExpressStep(requestedStep >= 0 ? requestedStep : 0);
  }, [onboardingParam, onboardingStepParam]);

  useEffect(() => {
    let active = true;
    if (!workspaceListReady) {
      setLoading(true);
      return () => {
        active = false;
      };
    }
    if (!selectedWorkspaceId) {
      setResidents([]);
      setError("Select a workspace to view residents.");
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    careService.residents.listMini({
      workspaceId: selectedWorkspaceId,
      wid: selectedWorkspaceId,
      size: 100,
    })
      .then((data) => {
        if (!active) return;
        setResidents((Array.isArray(data) ? data : []).map(normalizeResident));
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setResidents([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load residents.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [selectedWorkspaceId, workspaceListReady]);

  const totalAlerts = residents.reduce((sum, resident) => sum + resident.alerts, 0);
  const dueSoon = residents.filter((resident) => String(resident.ncpStatus).toUpperCase() !== "CURRENT").length;
  const visibleResidents = residents;
  const totalPages = Math.max(1, Math.ceil(visibleResidents.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedResidents = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return visibleResidents.slice(startIndex, startIndex + pageSize);
  }, [visibleResidents, currentPage, pageSize]);
  const paginationStart = visibleResidents.length ? ((currentPage - 1) * pageSize) + 1 : 0;
  const paginationEnd = Math.min(visibleResidents.length, currentPage * pageSize);
  const residentLimitReached = residents.length >= MAX_WORKSPACE_RESIDENTS;

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const updateExpressForm = (field, value) => setExpressForm((current) => ({ ...current, [field]: value }));

  const setBookmarkedExpressStep = (step, { replace = false } = {}) => {
    const nextStep = Math.max(0, Math.min(EXPRESS_ONBOARDING_STEPS.length - 1, step));
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("onboarding", "express");
    nextParams.set("step", EXPRESS_ONBOARDING_STEP_SLUGS[nextStep]);
    setExpressOpen(true);
    setExpressStep(nextStep);
    setSearchParams(nextParams, { replace });
  };

  const openExpressOnboarding = () => {
    setExpressError("");
    setBookmarkedExpressStep(0);
  };

  const closeExpressOnboarding = () => {
    if (expressSaving) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("onboarding");
    nextParams.delete("step");
    setSearchParams(nextParams);
    setExpressOpen(false);
    setExpressError("");
    setExpressForm(expressResidentForm());
    setExpressStep(0);
    setAssessmentFile(null);
    setAssessmentExtraction(null);
    if (profilePhotoPreview) URL.revokeObjectURL(profilePhotoPreview);
    setProfilePhotoFile(null);
    setProfilePhotoPreview("");
  };

  const applyResolvedAddress = (prefix, details, prediction) => {
    const fields = addressFromGooglePlace(details);
    const resolved = details?.formatted_address || prediction?.description || fields.addressLine1;
    const fieldName = (name) => prefix ? `${prefix}${name[0].toUpperCase()}${name.slice(1)}` : name;
    setExpressForm((current) => ({
      ...current,
      [fieldName("addressLine1")]: fields.addressLine1 || resolved || current[fieldName("addressLine1")],
      [fieldName("city")]: fields.city || "",
      [fieldName("county")]: fields.county || "",
      [fieldName("state")]: fields.state || "",
      [fieldName("country")]: fields.country || "",
      [fieldName("postalCode")]: fields.postalCode || "",
      [fieldName("latitude")]: fields.latitude,
      [fieldName("longitude")]: fields.longitude,
      [fieldName("placeId")]: fields.placeId,
    }));
  };

  const selectAssessmentForm = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setAssessmentFile(file);
    setAssessmentExtraction(null);
    setExpressError("");
  };

  const extractAssessmentForm = async () => {
    if (!assessmentFile || assessmentImporting) return;
    setAssessmentImporting(true);
    setExpressError("");
    try {
      const extraction = await careService.residents.importOnboardingAssessment([assessmentFile]);
      const imported = expressFormFromExtraction(extraction);
      setAssessmentExtraction(extraction);
      setExpressForm((current) => Object.entries(imported).reduce(
        (next, [field, value]) => value ? { ...next, [field]: value } : next,
        current,
      ));
      message.success("Assessment details were extracted. Review the populated fields.");
    } catch (err) {
      setAssessmentExtraction(null);
      setExpressError(err?.response?.data?.message || err?.message || "Unable to extract the assessment form.");
    } finally {
      setAssessmentImporting(false);
    }
  };

  const expressStepValid = () => {
    if (expressStep === 0 && (!expressForm.firstName.trim() || !expressForm.lastName.trim() || !expressForm.dateOfBirth)) {
      setExpressError("First name, last name, and date of birth are required.");
      return false;
    }
    if (expressStep === 0 && expressForm.dateOfBirth > new Date().toISOString().slice(0, 10)) {
      setExpressError("Date of birth cannot be in the future.");
      return false;
    }
    setExpressError("");
    return true;
  };

  const continueExpressOnboarding = () => {
    if (!expressStepValid()) return;
    setBookmarkedExpressStep(expressStep + 1);
  };

  const submitExpressOnboarding = async (event) => {
    event.preventDefault();
    if (expressSaving || residentLimitReached) return;
    const firstName = expressForm.firstName.trim();
    const middleName = expressForm.middleName.trim();
    const lastName = expressForm.lastName.trim();
    if (!firstName || !lastName || !expressForm.dateOfBirth) {
      setExpressError("First name, last name, and date of birth are required.");
      return;
    }
    if (expressForm.dateOfBirth > new Date().toISOString().slice(0, 10)) {
      setExpressError("Date of birth cannot be in the future.");
      return;
    }
    if (!expressForm.informationOnFileConfirmed) {
      setExpressError("Confirm that the resident's complete information is already on file.");
      return;
    }
    if (!selectedWorkspaceId) {
      setExpressError("Select a workspace before onboarding a resident.");
      return;
    }
    const residentName = [firstName, middleName, lastName].filter(Boolean).join(" ");
    const isNewResident = expressForm.residentType === "NEW";
    const initialAssessmentStage = isNewResident ? "THIRTY_DAY_PROBATION" : "ANNUAL_ASSESSMENT";
    const initialAssessmentScreen = isNewResident ? "30-days-probation" : "behavioral-notes";
    const personalInformation = {
      firstName,
      middleName,
      lastName,
      preferredName: expressForm.preferredName.trim(),
      birthDate: expressForm.dateOfBirth,
      dateOfBirth: expressForm.dateOfBirth,
      dob: expressForm.dateOfBirth,
      sex: expressForm.gender || null,
      gender: expressForm.gender || null,
      primaryLanguage: expressForm.primaryLanguage.trim(),
      heightFeet: expressForm.heightFeet === "" ? null : Number(expressForm.heightFeet),
      heightInches: expressForm.heightInches === "" ? null : Number(expressForm.heightInches),
      weightLbs: expressForm.weightLbs === "" ? null : Number(expressForm.weightLbs),
      race: expressForm.race || null,
      raceOther: expressForm.race === "Other" ? expressForm.raceOther.trim() || null : null,
      maritalStatus: expressForm.maritalStatus || null,
      placeOfBirth: expressForm.placeOfBirth.trim() || null,
      religion: expressForm.religion.trim() || null,
    };
    let residentPayload = {
      workspaceId: selectedWorkspaceId,
      wid: selectedWorkspaceId,
      status: isNewResident ? "PROSPECTIVE" : "ACTIVE",
      residentName,
      fullName: residentName,
      ...personalInformation,
      personalInformation,
      room: expressForm.room.trim() || null,
      roomNumber: expressForm.room.trim() || null,
      moveInDate: expressForm.moveInDate || null,
      admissionDate: expressForm.moveInDate || null,
      phone: expressForm.phone.trim() || null,
      email: expressForm.email.trim() || null,
      contactInformation: {
        phone: expressForm.phone.trim() || null,
        email: expressForm.email.trim() || null,
        emergencyContactName: expressForm.emergencyContactName.trim() || null,
        emergencyContactRelationship: expressForm.emergencyContactRelationship.trim() || null,
        emergencyContactRelationshipOther: expressForm.emergencyContactRelationship === "Other" ? expressForm.emergencyContactRelationshipOther.trim() : null,
        emergencyContactPhone: expressForm.emergencyContactPhone.trim() || null,
        emergencyContactEmail: expressForm.emergencyContactEmail.trim() || null,
      },
      locationInformation: {
        addressLine1: expressForm.addressLine1.trim() || null,
        addressLine2: expressForm.addressLine2.trim() || null,
        city: expressForm.city.trim() || null,
        county: expressForm.county.trim() || null,
        state: expressForm.state.trim() || null,
        country: expressForm.country.trim() || null,
        postalCode: expressForm.postalCode.trim() || null,
        latitude: expressForm.latitude,
        longitude: expressForm.longitude,
        placeId: expressForm.placeId || null,
      },
      residencyInformation: {
        room: expressForm.room.trim() || null,
        moveInDate: expressForm.moveInDate || null,
        notes: expressForm.residencyNotes.trim() || null,
      },
    };
    setExpressSaving(true);
    setExpressError("");
    try {
      if (profilePhotoFile) {
        const photoUpload = await uploadFile(null, profilePhotoFile, {
          confirmUpload: false,
          fileType: "PHOTO",
          includeUserId: false,
          preserveOriginal: true,
          purpose: "USER_PROFILE",
          skipUserValidation: true,
        });
        const profilePhotoFileId = photoUpload?.fileId || photoUpload?.file?.id || null;
        if (!profilePhotoFileId) throw new Error("The resident photo was uploaded without a file id.");
        residentPayload = { ...residentPayload, profilePhotoFileId };
      }
      let assessmentAttachment = null;
      if (assessmentFile) {
        const upload = await uploadFile(null, assessmentFile, {
          confirmUpload: true,
          fileType: assessmentFile.type || "DOCUMENT",
          includeUserId: false,
          preserveOriginal: true,
          purpose: "OTHER",
          skipUserValidation: true,
        });
        const fileId = upload?.fileId || upload?.file?.id || null;
        if (!fileId) throw new Error("The assessment attachment was uploaded without a file id.");
        assessmentAttachment = {
          fileId,
          fileName: assessmentFile.name,
          contentType: assessmentFile.type || null,
          confirmed: Boolean(upload?.confirmed),
        };
      }
      const resident = await careService.residents.create(residentPayload);
      const residentId = createdResidentId(resident);
      if (!residentId) throw new Error("Resident was created, but the API did not return a resident id.");
      const socialWorker = {
        firstName: expressForm.socialWorkerFirstName.trim(),
        lastName: expressForm.socialWorkerLastName.trim(),
        name: [expressForm.socialWorkerFirstName.trim(), expressForm.socialWorkerLastName.trim()].filter(Boolean).join(" "),
        agency: expressForm.socialWorkerAgency.trim(),
        relationship: expressForm.socialWorkerRelationship,
        relationshipOther: expressForm.socialWorkerRelationship === "Other" ? expressForm.socialWorkerRelationshipOther.trim() : "",
        phones: {
          mobile: expressForm.socialWorkerMobilePhone.trim(),
          office: expressForm.socialWorkerOfficePhone.trim(),
          home: expressForm.socialWorkerHomePhone.trim(),
          fax: expressForm.socialWorkerFax.trim(),
          preferred: expressForm[`socialWorker${expressForm.socialWorkerPreferredPhone[0].toUpperCase()}${expressForm.socialWorkerPreferredPhone.slice(1)}`]?.trim() || "",
        },
        fax: expressForm.socialWorkerFax.trim(),
        faxNumber: expressForm.socialWorkerFax.trim(),
        phone: expressForm.socialWorkerMobilePhone.trim() || expressForm.socialWorkerOfficePhone.trim() || expressForm.socialWorkerHomePhone.trim(),
        email: expressForm.socialWorkerEmail.trim(),
        notes: expressForm.socialWorkerNotes.trim(),
        address: {
          address: expressForm.socialWorkerAddressLine1.trim(),
          addressLine2: expressForm.socialWorkerAddressLine2.trim(),
          city: expressForm.socialWorkerCity.trim(),
          county: expressForm.socialWorkerCounty.trim(),
          state: expressForm.socialWorkerState.trim(),
          country: expressForm.socialWorkerCountry.trim(),
          zipCode: expressForm.socialWorkerPostalCode.trim(),
          latitude: expressForm.socialWorkerLatitude,
          longitude: expressForm.socialWorkerLongitude,
          placeId: expressForm.socialWorkerPlaceId,
        },
      };
      const hasSocialWorker = Boolean(socialWorker.name || socialWorker.phone || socialWorker.email || socialWorker.fax || socialWorker.agency);
      const hasEmergencyContact = Boolean(expressForm.emergencyContactName.trim() || expressForm.emergencyContactPhone.trim() || expressForm.emergencyContactEmail.trim() || expressForm.emergencyContactFax.trim());
      const hasPhysician = Boolean(expressForm.physicianFirstName.trim() || expressForm.physicianLastName.trim() || expressForm.physicianPractice.trim() || expressForm.physicianPhone.trim() || expressForm.physicianEmail.trim() || expressForm.physicianFax.trim());
      const processPayload = isNewResident ? {
        workspaceId: selectedWorkspaceId,
        wid: selectedWorkspaceId,
        assessmentType: "NEW_RESIDENT_ASSESSMENT",
        patientType: "NEW",
        residentId,
        residentName,
        name: `New Resident Care Process - ${residentName}`,
        processStartDate: expressForm.moveInDate,
        startDate: expressForm.moveInDate,
        status: "OPEN",
        workflowStatus: "OPEN",
        stage: initialAssessmentStage,
        currentStage: initialAssessmentStage,
        workflowStage: initialAssessmentStage,
        currentScreen: initialAssessmentScreen,
        initialStateAssessment: JSON.stringify({ status: "COMPLETE", stage: "STATE_INITIAL_ASSESSMENT", assessmentDate: expressForm.moveInDate, bioData: personalInformation, assessmentExtraction }),
        assessmentExtraction,
        assessmentAttachment,
        assessmentAttachments: assessmentAttachment ? [assessmentAttachment] : [],
        socialWorker: hasSocialWorker ? socialWorker : null,
      } : {
        workspaceId: selectedWorkspaceId,
        wid: selectedWorkspaceId,
        assessmentType: "ANNUAL_ASSESSMENT",
        patientType: "RESIDENT",
        residentId,
        residentName,
        name: `Annual Resident Care Process - ${residentName}`,
        processStartDate: expressForm.moveInDate,
        startDate: expressForm.moveInDate,
        status: "OPEN",
        workflowStatus: "OPEN",
        stage: initialAssessmentStage,
        currentStage: initialAssessmentStage,
        workflowStage: initialAssessmentStage,
        currentScreen: initialAssessmentScreen,
        socialWorker: hasSocialWorker ? socialWorker : null,
        assessmentExtraction,
        assessmentAttachment,
        assessmentAttachments: assessmentAttachment ? [assessmentAttachment] : [],
      };
      await careService.assessmentProcesses.create(processPayload);
      if (hasSocialWorker) {
        await careService.residentContacts.createForResident(residentId, {
          ...socialWorker,
          relationship: "SOCIAL_WORKER",
          relationshipType: "SOCIAL_WORKER",
          contactType: "SOCIAL_WORKER",
        });
      }
      if (hasEmergencyContact) await careService.residentContacts.createForResident(residentId, {
        name: expressForm.emergencyContactName.trim(),
        relationship: expressForm.emergencyContactRelationship,
        relationshipOther: expressForm.emergencyContactRelationship === "Other" ? expressForm.emergencyContactRelationshipOther.trim() : "",
        relationshipType: "EMERGENCY_CONTACT",
        contactType: "EMERGENCY_CONTACT",
        phone: expressForm.emergencyContactPhone.trim(),
        fax: expressForm.emergencyContactFax.trim(),
        faxNumber: expressForm.emergencyContactFax.trim(),
        email: expressForm.emergencyContactEmail.trim(),
        phones: {
          mobile: expressForm.emergencyContactPhone.trim(),
          fax: expressForm.emergencyContactFax.trim(),
          preferred: expressForm.emergencyContactPhone.trim(),
        },
      });
      if (hasPhysician) await careService.residentContacts.createForResident(residentId, {
        firstName: expressForm.physicianFirstName.trim(),
        lastName: expressForm.physicianLastName.trim(),
        name: [expressForm.physicianFirstName.trim(), expressForm.physicianLastName.trim()].filter(Boolean).join(" "),
        relationship: expressForm.physicianType,
        relationshipType: expressForm.physicianType === "Specialist" ? "SPECIALIST" : "PRIMARY_CARE_DOCTOR",
        contactType: expressForm.physicianType === "Specialist" ? "SPECIALIST" : "PRIMARY_CARE_DOCTOR",
        agency: expressForm.physicianPractice.trim(),
        phone: expressForm.physicianPhone.trim(),
        fax: expressForm.physicianFax.trim(),
        faxNumber: expressForm.physicianFax.trim(),
        email: expressForm.physicianEmail.trim(),
        phones: { office: expressForm.physicianPhone.trim(), fax: expressForm.physicianFax.trim(), preferred: expressForm.physicianPhone.trim() },
        address: {
          address: expressForm.physicianAddressLine1.trim(),
          addressLine2: expressForm.physicianAddressLine2.trim(),
          city: expressForm.physicianCity.trim(),
          county: expressForm.physicianCounty.trim(),
          state: expressForm.physicianState.trim(),
          country: expressForm.physicianCountry.trim(),
          zipCode: expressForm.physicianPostalCode.trim(),
          latitude: expressForm.physicianLatitude,
          longitude: expressForm.physicianLongitude,
          placeId: expressForm.physicianPlaceId,
        },
      });
      message.success(`${residentName} was onboarded successfully.`);
      setResidents((current) => [normalizeResident({ ...residentPayload, ...resident, id: residentId }), ...current]);
      setExpressOpen(false);
      setExpressForm(expressResidentForm());
      setExpressStep(0);
      setAssessmentFile(null);
      setAssessmentExtraction(null);
      if (profilePhotoPreview) URL.revokeObjectURL(profilePhotoPreview);
      setProfilePhotoFile(null);
      setProfilePhotoPreview("");
      navigate(`/residents/${residentId}`);
    } catch (err) {
      setExpressError(err?.response?.data?.message || err?.message || "Unable to complete express onboarding.");
    } finally {
      setExpressSaving(false);
    }
  };

  return (
    <CareShell className={`dashboard-content residents-page ${expressOpen ? "is-express-onboarding" : ""}`}>
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Facility Residents</h1>
          <p>Residents currently assigned to Sunset Gardens Assisted Living.</p>
        </div>
        <div className="residents-actions">
          <button className="mini-btn"><i className="ti ti-filter" /> Filter</button>
          <Guard roles={["care_owner", "care_admin"]}>
            {residentLimitReached ? (
              <button
                className="btn resident-limit-action"
                type="button"
                disabled
                title={`This workspace has reached its ${MAX_WORKSPACE_RESIDENTS}-resident limit.`}
              >
                <i className="ti ti-users" /> Resident Limit Reached
              </button>
            ) : (
              <>
                <button className="btn express-onboarding-launch" type="button" onClick={openExpressOnboarding}><i className="ti ti-bolt" /> Express Onboarding</button>
              </>
            )}
          </Guard>
        </div>
      </div>

      <div className="resident-summary-grid">
        {[
          ["ti-users", "Total residents", residents.length, "In this workspace"],
          ["ti-heart-handshake", "NCP follow-up", dueSoon, "Need review or update"],
          ["ti-alert-triangle", "Open alerts", totalAlerts, "Across all residents"],
          ["ti-notes", "Daily notes", "24", "Captured this week"],
        ].map(([icon, label, value, note]) => (
          <article className="resident-summary-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{value}</b><small>{note}</small></div>
          </article>
        ))}
      </div>

      <Panel title="Residents">
        {error ? <p className="form-error">{error}</p> : null}
        <div className="resident-list-toolbar">
          <label className="resident-search">
            <i className="ti ti-search" />
            <input placeholder="Search residents by name, room, contact, or care team" />
          </label>
          <div className="resident-filter-pills">
            <button className="is-active">All</button>
            <button>NCP Due</button>
            <button>Alerts</button>
            <button>Active</button>
          </div>
        </div>

        <div className="resident-directory">
          <div className="resident-directory-row resident-directory-head">
            <span>Resident</span><span>Preferred name</span><span>Gender</span><span>City</span><span>State</span><span>Status</span><span />
          </div>
          {loading ? <p>Loading residents...</p> : null}
          {!loading && !visibleResidents.length ? <p>No residents found.</p> : null}
          {paginatedResidents.map((resident) => (
            <Link className="resident-directory-row" to={residentHref(resident)} key={resident.id || resident.name}>
              <span className="resident-directory-person">
                <b>{resident.initials}</b>
                <span><strong>{resident.name}</strong><small>{resident.room ? `Room ${resident.room}` : "No room assigned"}</small></span>
              </span>
              <span className="resident-directory-preferred">{resident.preferredName || "Not added"}</span>
              <span className="resident-directory-gender">{resident.gender || "Not added"}</span>
              <span className="resident-directory-city">{resident.city || "Not added"}</span>
              <span className="resident-directory-state">{resident.state || "Not added"}</span>
              <span className="resident-directory-mobile-meta">
                {resident.preferredName ? <small>Preferred: {resident.preferredName}</small> : null}
                {resident.gender ? <small>{resident.gender}</small> : null}
                {resident.city || resident.state ? <small>{[resident.city, resident.state].filter(Boolean).join(", ")}</small> : null}
              </span>
              <span className="resident-directory-status"><em className={`pill ${resident.status === "ACTIVE" ? "low" : resident.status === "PROSPECTIVE" ? "blue" : "medium"}`}>{resident.status || "Unknown"}</em></span>
              <span className="resident-row-actions">{resident.alerts ? <em>{resident.alerts}</em> : null}<i className="ti ti-chevron-right" /></span>
            </Link>
          ))}
        </div>
        {visibleResidents.length ? (
          <div className="timesheet-pagination resident-pagination">
            <span>
              Showing <strong>{paginationStart}-{paginationEnd}</strong> of <strong>{visibleResidents.length}</strong>
            </span>
            <label>
              Rows
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                {RESIDENT_PAGE_SIZE_OPTIONS.map((size) => (
                  <option value={size} key={size}>{size}</option>
                ))}
              </select>
            </label>
            <div className="timesheet-pagination-actions">
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={currentPage <= 1}
              >
                <i className="ti ti-chevron-left" /> Previous
              </button>
              <strong>Page {currentPage} of {totalPages}</strong>
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={currentPage >= totalPages}
              >
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
        ) : null}
      </Panel>

      {expressOpen ? <section className="express-resident-page">
        <header className="express-resident-page-head">
          <div>
            <span className="page-eyebrow">Residents</span>
            <h1>Express Resident Onboarding</h1>
            <p>Complete the essential resident record and create the appropriate resident care process.</p>
          </div>
          <button className="btn secondary" type="button" disabled={expressSaving} onClick={closeExpressOnboarding}><i className="ti ti-arrow-left" /> Back to Residents</button>
        </header>
        <form className="express-resident-form" onSubmit={submitExpressOnboarding}>
          <div className="express-resident-intro">
            <i className="ti ti-bolt" />
            <div><strong>{EXPRESS_ONBOARDING_STEPS[expressStep]}</strong><span>Step {expressStep + 1} of {EXPRESS_ONBOARDING_STEPS.length}</span></div>
          </div>
          <div className="express-resident-progress" aria-label="Express onboarding progress">
            {EXPRESS_ONBOARDING_STEPS.map((step, index) => (
              <button className={index === expressStep ? "active" : index < expressStep ? "complete" : ""} type="button" disabled={index > expressStep} onClick={() => { setExpressError(""); setBookmarkedExpressStep(index); }} key={step}>
                <span>{index < expressStep ? <i className="ti ti-check" /> : index + 1}</span><small>{step}</small>
              </button>
            ))}
          </div>
          {expressError ? <p className="form-error">{expressError}</p> : null}
          {expressStep === 0 ? <fieldset><legend>Resident information</legend>
            <div className="express-assessment-import">
              <div><i className="ti ti-file-upload" /><span><strong>Add assessment form</strong><small>Upload an assessment to extract and prefill resident details.</small></span></div>
              <label className="btn secondary">
                <i className="ti ti-paperclip" /> {assessmentFile ? "Replace attachment" : "Choose attachment"}
                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.heic,.heif,application/pdf,image/*" disabled={assessmentImporting} onChange={selectAssessmentForm} />
              </label>
              {assessmentFile ? <div className="express-assessment-file"><i className="ti ti-file-check" /><span><strong>{assessmentFile.name}</strong><small>{assessmentExtraction ? "Details extracted" : "Attached. Extraction is optional."}</small></span>{!assessmentExtraction ? <button className="extract" type="button" disabled={assessmentImporting} onClick={extractAssessmentForm}><i className={`ti ${assessmentImporting ? "ti-loader-2" : "ti-scan"}`} /> {assessmentImporting ? "Extracting..." : "Extract Details"}</button> : null}<button type="button" title="Remove attachment" onClick={() => { setAssessmentFile(null); setAssessmentExtraction(null); }}><i className="ti ti-x" /></button></div> : null}
            </div>
            <div className="express-resident-type-toggle">
              <button className={expressForm.residentType === "NEW" ? "active" : ""} type="button" onClick={() => updateExpressForm("residentType", "NEW")}><i className="ti ti-user-plus" /> New resident</button>
              <button className={expressForm.residentType === "EXISTING" ? "active" : ""} type="button" onClick={() => updateExpressForm("residentType", "EXISTING")}><i className="ti ti-user-check" /> Existing resident</button>
            </div>
            <small>{expressForm.residentType === "NEW" ? "Creates a new resident care process." : "Creates an annual care process for a current resident."}</small>
            <div className="express-resident-grid split-columns">
              <div className="express-field-column">
                <h3><i className="ti ti-user" /> Resident name</h3>
                <ResidentProfilePhoto
                  src={profilePhotoPreview}
                  name={[expressForm.firstName, expressForm.lastName].filter(Boolean).join(" ") || "Resident"}
                  onChange={(file) => {
                    if (profilePhotoPreview?.startsWith("blob:")) URL.revokeObjectURL(profilePhotoPreview);
                    setProfilePhotoFile(file);
                    setProfilePhotoPreview(URL.createObjectURL(file));
                  }}
                  onRemove={() => {
                    if (profilePhotoPreview?.startsWith("blob:")) URL.revokeObjectURL(profilePhotoPreview);
                    setProfilePhotoFile(null);
                    setProfilePhotoPreview("");
                  }}
                  helperText="Click the photo to view it. Use the pencil to update it."
                />
                <label>First name<input autoFocus value={expressForm.firstName} onChange={(event) => updateExpressForm("firstName", event.target.value)} /></label>
                <label>Middle name<input value={expressForm.middleName} onChange={(event) => updateExpressForm("middleName", event.target.value)} /></label>
                <label>Last name<input value={expressForm.lastName} onChange={(event) => updateExpressForm("lastName", event.target.value)} /></label>
                <label>Preferred name<input value={expressForm.preferredName} onChange={(event) => updateExpressForm("preferredName", event.target.value)} /></label>
              </div>
              <div className="express-field-column resident-details-column">
                <h3><i className="ti ti-id" /> Resident details</h3>
                <label>Date of birth<input type="date" max={new Date().toISOString().slice(0, 10)} value={expressForm.dateOfBirth} onChange={(event) => updateExpressForm("dateOfBirth", event.target.value)} /></label>
                <label>Gender<select value={expressForm.gender} onChange={(event) => updateExpressForm("gender", event.target.value)}><option value="">Select</option><option value="FEMALE">Female</option><option value="MALE">Male</option><option value="NON_BINARY">Non-binary</option><option value="OTHER">Other</option><option value="UNKNOWN">Prefer not to say</option></select></label>
                <label>Primary language<select value={expressForm.primaryLanguage} onChange={(event) => updateExpressForm("primaryLanguage", event.target.value)}>
                  {!PRIMARY_LANGUAGE_OPTIONS.includes(expressForm.primaryLanguage) && expressForm.primaryLanguage ? <option value={expressForm.primaryLanguage}>{expressForm.primaryLanguage}</option> : null}
                  {PRIMARY_LANGUAGE_OPTIONS.map((language) => <option value={language} key={language}>{language}</option>)}
                </select></label>
                <label>Race<select value={expressForm.race} onChange={(event) => updateExpressForm("race", event.target.value)}><option value="">Select race</option>{RACE_OPTIONS.map((race) => <option value={race} key={race}>{race}</option>)}</select></label>
                {expressForm.race === "Other" ? <label>Specify race<input value={expressForm.raceOther} onChange={(event) => updateExpressForm("raceOther", event.target.value)} /></label> : null}
                <label>Marital status<select value={expressForm.maritalStatus} onChange={(event) => updateExpressForm("maritalStatus", event.target.value)}><option value="">Select marital status</option>{MARITAL_STATUS_OPTIONS.map((status) => <option value={status} key={status}>{status}</option>)}</select></label>
                <label>Height (ft-in)<span className="resident-height-fields"><input aria-label="Height feet" min="1" max="9" placeholder="ft" type="number" value={expressForm.heightFeet} onChange={(event) => updateExpressForm("heightFeet", event.target.value)} /><input aria-label="Height inches" min="0" max="11" placeholder="in" type="number" value={expressForm.heightInches} onChange={(event) => updateExpressForm("heightInches", event.target.value)} /></span></label>
                <label>Weight (lbs)<input min="1" step="0.1" type="number" value={expressForm.weightLbs} onChange={(event) => updateExpressForm("weightLbs", event.target.value)} /></label>
                <label>Place of Birth<input value={expressForm.placeOfBirth} onChange={(event) => updateExpressForm("placeOfBirth", event.target.value)} /></label>
                <label>Religion<input value={expressForm.religion} onChange={(event) => updateExpressForm("religion", event.target.value)} /></label>
              </div>
            </div>
          </fieldset> : null}
          {expressStep === 1 ? <fieldset><legend>Contact information</legend><div className="express-resident-grid has-address-column">
            <div className="express-field-column">
              <h3><i className="ti ti-phone" /> Contact details</h3>
              <label>Phone<input type="tel" value={expressForm.phone} onChange={(event) => updateExpressForm("phone", event.target.value)} /></label>
              <label>Email<input type="email" value={expressForm.email} onChange={(event) => updateExpressForm("email", event.target.value)} /></label>
            </div>
            <div className="express-field-column address-column">
              <h3><i className="ti ti-map-pin" /> Address information</h3>
              <label>Search address<GooglePlacesAutocompleteInput googleMapsApiKey={googleMapsApiKey} placeholder={appConfigLoading ? "Loading address search..." : "Search resident address"} types={["address"]} value={expressForm.addressLine1} onChange={(value) => updateExpressForm("addressLine1", value)} onResolvePlace={({ details, prediction }) => applyResolvedAddress("", details, prediction)} /></label>
              <label>Address line 1<input value={expressForm.addressLine1} onChange={(event) => updateExpressForm("addressLine1", event.target.value)} /></label>
              <label>Address line 2<input value={expressForm.addressLine2} onChange={(event) => updateExpressForm("addressLine2", event.target.value)} /></label>
              <div className="express-field-pair"><label>City<input value={expressForm.city} onChange={(event) => updateExpressForm("city", event.target.value)} /></label><label>County<input value={expressForm.county} onChange={(event) => updateExpressForm("county", event.target.value)} /></label></div>
              <div className="express-field-triplet"><label>State<input value={expressForm.state} maxLength={2} onChange={(event) => updateExpressForm("state", event.target.value.toUpperCase())} /></label><label>ZIP code<input inputMode="numeric" value={expressForm.postalCode} onChange={(event) => updateExpressForm("postalCode", event.target.value)} /></label><label>Country<input value={expressForm.country} maxLength={2} onChange={(event) => updateExpressForm("country", event.target.value.toUpperCase())} /></label></div>
            </div>
          </div></fieldset> : null}
          {expressStep === 2 ? <fieldset><legend>Emergency contact</legend><div className="express-resident-grid">
            <label>Contact name<input value={expressForm.emergencyContactName} onChange={(event) => updateExpressForm("emergencyContactName", event.target.value)} /></label>
            <label>Relationship<select value={expressForm.emergencyContactRelationship} onChange={(event) => updateExpressForm("emergencyContactRelationship", event.target.value)}><option value="">Select relationship</option>{CONTACT_RELATIONSHIP_OPTIONS.map((relationship) => <option value={relationship} key={relationship}>{relationship}</option>)}</select></label>
            {expressForm.emergencyContactRelationship === "Other" ? <label>Specify relationship<input value={expressForm.emergencyContactRelationshipOther} onChange={(event) => updateExpressForm("emergencyContactRelationshipOther", event.target.value)} /></label> : null}
            <label>Phone<input type="tel" value={expressForm.emergencyContactPhone} onChange={(event) => updateExpressForm("emergencyContactPhone", event.target.value)} /></label>
            <label>Fax<input type="tel" value={expressForm.emergencyContactFax} onChange={(event) => updateExpressForm("emergencyContactFax", event.target.value)} /></label>
            <label>Email<input type="email" value={expressForm.emergencyContactEmail} onChange={(event) => updateExpressForm("emergencyContactEmail", event.target.value)} /></label>
          </div></fieldset> : null}
          {expressStep === 3 ? <fieldset><legend>Doctor / physician contact</legend><div className="express-resident-grid has-address-column">
            <div className="express-field-column">
              <h3><i className="ti ti-stethoscope" /> Provider details</h3>
              <div className="express-field-pair"><label>First name<input value={expressForm.physicianFirstName} onChange={(event) => updateExpressForm("physicianFirstName", event.target.value)} /></label><label>Last name<input value={expressForm.physicianLastName} onChange={(event) => updateExpressForm("physicianLastName", event.target.value)} /></label></div>
              <label>Provider type<select value={expressForm.physicianType} onChange={(event) => updateExpressForm("physicianType", event.target.value)}><option>Primary Care Doctor</option><option>Specialist</option></select></label>
              <label>Practice or clinic<input value={expressForm.physicianPractice} onChange={(event) => updateExpressForm("physicianPractice", event.target.value)} /></label>
              <label>Phone<input type="tel" value={expressForm.physicianPhone} onChange={(event) => updateExpressForm("physicianPhone", event.target.value)} /></label>
              <label>Fax<input type="tel" value={expressForm.physicianFax} onChange={(event) => updateExpressForm("physicianFax", event.target.value)} /></label>
              <label>Email<input type="email" value={expressForm.physicianEmail} onChange={(event) => updateExpressForm("physicianEmail", event.target.value)} /></label>
            </div>
            <div className="express-field-column address-column">
              <h3><i className="ti ti-map-pin" /> Address information</h3>
              <label>Search address<GooglePlacesAutocompleteInput googleMapsApiKey={googleMapsApiKey} placeholder={appConfigLoading ? "Loading address search..." : "Search doctor or clinic address"} types={["address"]} value={expressForm.physicianAddressLine1} onChange={(value) => updateExpressForm("physicianAddressLine1", value)} onResolvePlace={({ details, prediction }) => applyResolvedAddress("physician", details, prediction)} /></label>
              <label>Address line 1<input value={expressForm.physicianAddressLine1} onChange={(event) => updateExpressForm("physicianAddressLine1", event.target.value)} /></label>
              <label>Address line 2<input value={expressForm.physicianAddressLine2} onChange={(event) => updateExpressForm("physicianAddressLine2", event.target.value)} /></label>
              <div className="express-field-pair"><label>City<input value={expressForm.physicianCity} onChange={(event) => updateExpressForm("physicianCity", event.target.value)} /></label><label>County<input value={expressForm.physicianCounty} onChange={(event) => updateExpressForm("physicianCounty", event.target.value)} /></label></div>
              <div className="express-field-triplet"><label>State<input maxLength={2} value={expressForm.physicianState} onChange={(event) => updateExpressForm("physicianState", event.target.value.toUpperCase())} /></label><label>ZIP code<input value={expressForm.physicianPostalCode} onChange={(event) => updateExpressForm("physicianPostalCode", event.target.value)} /></label><label>Country<input maxLength={2} value={expressForm.physicianCountry} onChange={(event) => updateExpressForm("physicianCountry", event.target.value.toUpperCase())} /></label></div>
            </div>
          </div></fieldset> : null}
          {expressStep === 4 ? <fieldset><legend>Social worker / case manager contact</legend><div className="express-resident-grid has-address-column">
            <div className="express-field-column">
              <h3><i className="ti ti-user-heart" /> Contact details</h3>
              <div className="express-field-pair"><label>First name<input value={expressForm.socialWorkerFirstName} onChange={(event) => updateExpressForm("socialWorkerFirstName", event.target.value)} /></label><label>Last name<input value={expressForm.socialWorkerLastName} onChange={(event) => updateExpressForm("socialWorkerLastName", event.target.value)} /></label></div>
              <label>Relationship<select value={expressForm.socialWorkerRelationship} onChange={(event) => updateExpressForm("socialWorkerRelationship", event.target.value)}>{CONTACT_RELATIONSHIP_OPTIONS.map((relationship) => <option value={relationship} key={relationship}>{relationship}</option>)}</select></label>
              {expressForm.socialWorkerRelationship === "Other" ? <label>Specify relationship<input value={expressForm.socialWorkerRelationshipOther} onChange={(event) => updateExpressForm("socialWorkerRelationshipOther", event.target.value)} /></label> : null}
              <label>Agency or organization<input value={expressForm.socialWorkerAgency} onChange={(event) => updateExpressForm("socialWorkerAgency", event.target.value)} /></label>
              <div className="express-field-pair"><label>Mobile phone<input type="tel" value={expressForm.socialWorkerMobilePhone} onChange={(event) => updateExpressForm("socialWorkerMobilePhone", event.target.value)} /></label><label>Office phone<input type="tel" value={expressForm.socialWorkerOfficePhone} onChange={(event) => updateExpressForm("socialWorkerOfficePhone", event.target.value)} /></label></div>
              <div className="express-field-pair"><label>Home phone<input type="tel" value={expressForm.socialWorkerHomePhone} onChange={(event) => updateExpressForm("socialWorkerHomePhone", event.target.value)} /></label><label>Preferred phone<select value={expressForm.socialWorkerPreferredPhone} onChange={(event) => updateExpressForm("socialWorkerPreferredPhone", event.target.value)}><option value="mobilePhone">Mobile</option><option value="officePhone">Office</option><option value="homePhone">Home</option></select></label></div>
              <label>Fax<input type="tel" value={expressForm.socialWorkerFax} onChange={(event) => updateExpressForm("socialWorkerFax", event.target.value)} /></label>
              <label>Email<input type="email" value={expressForm.socialWorkerEmail} onChange={(event) => updateExpressForm("socialWorkerEmail", event.target.value)} /></label>
              <label>Notes<textarea rows={3} value={expressForm.socialWorkerNotes} onChange={(event) => updateExpressForm("socialWorkerNotes", event.target.value)} /></label>
            </div>
            <div className="express-field-column address-column">
              <h3><i className="ti ti-map-pin" /> Address information</h3>
              <label>Search address<GooglePlacesAutocompleteInput googleMapsApiKey={googleMapsApiKey} placeholder={appConfigLoading ? "Loading address search..." : "Search social worker address"} types={["address"]} value={expressForm.socialWorkerAddressLine1} onChange={(value) => updateExpressForm("socialWorkerAddressLine1", value)} onResolvePlace={({ details, prediction }) => applyResolvedAddress("socialWorker", details, prediction)} /></label>
              <label>Address line 1<input value={expressForm.socialWorkerAddressLine1} onChange={(event) => updateExpressForm("socialWorkerAddressLine1", event.target.value)} /></label>
              <label>Address line 2<input value={expressForm.socialWorkerAddressLine2} onChange={(event) => updateExpressForm("socialWorkerAddressLine2", event.target.value)} /></label>
              <div className="express-field-pair"><label>City<input value={expressForm.socialWorkerCity} onChange={(event) => updateExpressForm("socialWorkerCity", event.target.value)} /></label><label>County<input value={expressForm.socialWorkerCounty} onChange={(event) => updateExpressForm("socialWorkerCounty", event.target.value)} /></label></div>
              <div className="express-field-triplet"><label>State<input maxLength={2} value={expressForm.socialWorkerState} onChange={(event) => updateExpressForm("socialWorkerState", event.target.value.toUpperCase())} /></label><label>ZIP code<input value={expressForm.socialWorkerPostalCode} onChange={(event) => updateExpressForm("socialWorkerPostalCode", event.target.value)} /></label><label>Country<input maxLength={2} value={expressForm.socialWorkerCountry} onChange={(event) => updateExpressForm("socialWorkerCountry", event.target.value.toUpperCase())} /></label></div>
            </div>
          </div></fieldset> : null}
          {expressStep === 5 ? <fieldset><legend>Residency information</legend><div className="express-resident-grid">
            <label>Room<input value={expressForm.room} onChange={(event) => updateExpressForm("room", event.target.value)} /></label>
            <label>Move-in date<input type="date" value={expressForm.moveInDate} onChange={(event) => updateExpressForm("moveInDate", event.target.value)} /></label>
            <label className="wide">Residency notes<textarea rows={4} value={expressForm.residencyNotes} onChange={(event) => updateExpressForm("residencyNotes", event.target.value)} placeholder="Add relevant placement or residency details already held on file." /></label>
          </div></fieldset> : null}
          {expressStep === 6 ? <fieldset><legend>Review and confirm</legend>
            <div className="express-resident-review">
              <div><span>Resident</span><strong>{[expressForm.firstName, expressForm.middleName, expressForm.lastName].filter(Boolean).join(" ")}</strong></div>
              <div><span>Resident status</span><strong>{expressForm.residentType === "NEW" ? "New resident" : "Existing resident"}</strong></div>
              <div><span>Contact</span><strong>{expressForm.phone || expressForm.email || "Not added"}</strong></div>
              <div><span>Emergency contact</span><strong>{expressForm.emergencyContactName || "Not added"}</strong></div>
              <div><span>Doctor / physician</span><strong>{[expressForm.physicianFirstName, expressForm.physicianLastName].filter(Boolean).join(" ") || "Not added"}</strong></div>
              <div><span>Social worker / case manager</span><strong>{[expressForm.socialWorkerFirstName, expressForm.socialWorkerLastName].filter(Boolean).join(" ") || "Not added"}</strong></div>
              <div><span>Residency</span><strong>{[expressForm.room && `Room ${expressForm.room}`, expressForm.moveInDate].filter(Boolean).join(" - ")}</strong></div>
              <div><span>Resident care process</span><strong>{expressForm.residentType === "NEW" ? "New Resident Care Process" : "Annual Resident Care Process"}</strong></div>
              <div><span>Starting stage</span><strong>{expressForm.residentType === "NEW" ? "30 Days Probation" : "Daily Notes"}</strong></div>
            </div>
            <label className="express-resident-confirm"><input type="checkbox" checked={expressForm.informationOnFileConfirmed} onChange={(event) => updateExpressForm("informationOnFileConfirmed", event.target.checked)} /><span>I confirm that we already have all required information and supporting records for this resident on file.</span></label>
          </fieldset> : null}
          <div className="drawer-actions">
            <button className="btn secondary" type="button" disabled={expressSaving} onClick={closeExpressOnboarding}>Cancel</button>
            {expressStep > 0 ? <button className="btn secondary" type="button" disabled={expressSaving} onClick={() => { setExpressError(""); setBookmarkedExpressStep(expressStep - 1); }}><i className="ti ti-arrow-left" /> Back</button> : null}
            {expressStep < EXPRESS_ONBOARDING_STEPS.length - 1
              ? <button className="btn primary" type="button" onClick={continueExpressOnboarding}>Continue <i className="ti ti-arrow-right" /></button>
              : <button className="btn primary" type="submit" disabled={expressSaving || !expressForm.informationOnFileConfirmed}>{expressSaving ? "Onboarding..." : "Onboard Resident"}</button>}
          </div>
        </form>
      </section> : null}
    </CareShell>
  );
}
